import {
  useSubscriptionAccessConfigContext,
  AppConstants,
} from '@ltvco/refresh-lib/ctx';
import {
  useCreateFamilyTree,
  formatFamilyTreeDataFromDataCollection,
  findRecordInDataCollection,
  useGetDataCollectionRecords,
  useFocusedFamilyTreeMember,
} from '@ltvco/refresh-lib/fmt';
import { useRecordByCollectionId } from '@ltvco/refresh-lib/hooks';
import { Grid, Box, styled } from '@ltvco/refresh-lib/theme';
import { ReportLoading } from '@ltvco/refresh-lib/v1';
import {
  IndexReportOverview,
  ReportNullState,
  SearchFooter,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const FooterSearchContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2.5),

  // Not only the ReportBody, but also the AppContent (App.tsx) are limited to 1600px max width
  // So with this, we make sure the footer search is always 100% of the screen width
  // Use negative margins to break out of the centered container without causing overflow
  [theme.breakpoints.up(1600)]: {
    width: '100vw',
    marginLeft: 'calc(-50vw + 50%)',
    marginRight: 'calc(-50vw + 50%)',
  },
}));

export function RecordPage() {
  const {
    dataCollections: { structuredCollections = [] },
  } = useContext(AppConstants);
  const { record_id, collection_id } = useParams();
  const navigate = useNavigate();
  const [isRecordReady, setIsRecordReady] = useState(false);
  const [recordError, setRecordError] = useState(false);
  const { createFamilyTree, isCreatingFamilyTree } = useCreateFamilyTree();
  const { navigateToFocusedMember } = useFocusedFamilyTreeMember();
  const {
    data: dataCollectionRecords,
    isLoading: isDataCollectionRecordsLoading,
    error: errorDataCollectionRecords,
  } = useGetDataCollectionRecords({
    bodyOptions: {
      placeholders_excluded: true,
    },
    logNotFoundErrorEnabled: false,
  });

  const recordInTreeDataCollection = findRecordInDataCollection(
    record_id as string,
    collection_id as string,
    dataCollectionRecords?.records
  );
  const recordInTree = !!recordInTreeDataCollection;
  const noTreeCreated = errorDataCollectionRecords?.message?.includes('404');

  const { vinVariation } = useSubscriptionAccessConfigContext();
  const isInHiddenFooterSearchVariation = useFeatureIsOn('prop-3137');

  // Use the hook that fetches records and automatically creates BBQ reports
  const {
    data: recordToShow,
    isLoading,
    error: hasError,
  } = useRecordByCollectionId(record_id as string, collection_id as string);
  // Effect to manage record loading state and prevent flickering
  useEffect(() => {
    if (isLoading) {
      setIsRecordReady(false);
      setRecordError(false);
      return;
    }

    if (hasError) {
      setRecordError(true);
      setIsRecordReady(false);
      return;
    }

    // Check if we have a valid record
    if (recordToShow) {
      setIsRecordReady(true);
      setRecordError(false);
    } else {
      setRecordError(true);
      setIsRecordReady(false);
    }
  }, [isLoading, hasError, recordToShow]);

  // Show loading while data is being fetched or state is being determined
  if (!isRecordReady) {
    return <ReportLoading menuItems={2} />;
  }

  // Show error state if there's an error or no valid record
  if (recordError || !recordToShow) {
    return <ReportNullState />;
  }

  const handleAddToFamilyTree = async () => {
    if (isDataCollectionRecordsLoading) return;
    if (recordInTree) {
      // Use the record we already found to get the member_id
      const memberId = recordInTreeDataCollection?.member_id;
      navigateToFocusedMember(memberId as number);
      return;
    }

    const postIndi = formatFamilyTreeDataFromDataCollection(
      recordToShow,
      collection_id as string,
      record_id as string
    );

    if (noTreeCreated) {
      await createFamilyTree({
        indi: postIndi,
      });
      return;
    }

    localStorage.setItem('familyTreeRelativeToAdd', JSON.stringify(postIndi));

    navigate('/family-tree');
  };

  const structuredCollection = structuredCollections.find(
    (c) => c.id === collection_id
  );
  const structuredRecordType = structuredCollection?.type;

  return (
    <Box bgcolor={'background.paper'}>
      <Grid container justifyContent={'center'} direction={'row'} width="100%">
        <IndexReportOverview
          record={recordToShow}
          recordInFamilyTree={recordInTree}
          isCreatingFamilyTree={isCreatingFamilyTree}
          handleAddToFamilyTree={handleAddToFamilyTree}
          collectionType={structuredRecordType}
        />
      </Grid>
      {!isInHiddenFooterSearchVariation && !vinVariation && (
        <FooterSearchContainer>
          <SearchFooter hideComponentForIndexCollection={true} />
        </FooterSearchContainer>
      )}
    </Box>
  );
}
